import { X, Server, Database, HardDrive, Cpu, Layers, Box, Users, RotateCcw, RefreshCw } from "lucide-react"
import { useState } from "react"
import { useSystemStore, type TopoNode, type NodeHealth } from "@/store/systemStore"
import { restartService } from "@/lib/api"

const NODE_ICONS: Record<string, typeof Server> = {
  users: Users,
  api: Server,
  queue: Layers,
  worker: Cpu,
  gpu: HardDrive,
  database: Database,
  storage: Box,
}

const HEALTH_LABELS: Record<NodeHealth, { color: string; label: string }> = {
  healthy:  { color: "#22c55e", label: "Healthy" },
  degraded: { color: "#f59e0b", label: "Degraded" },
  critical: { color: "#ef4444", label: "Critical" },
  unknown:  { color: "#6b7280", label: "Unknown" },
}

export default function NodeDetailDrawer() {
  const selectedNodeId = useSystemStore(s => s.selectedNodeId)
  const topoNodes = useSystemStore(s => s.topoNodes)
  const selectNode = useSystemStore(s => s.selectNode)
  const snapshot = useSystemStore(s => s.snapshot)
  const [restarting, setRestarting] = useState(false)
  const [message, setMessage] = useState("")

  const node = topoNodes.find(n => n.id === selectedNodeId)
  if (!node) return null

  const Icon = NODE_ICONS[node.type] || Server
  const health = HEALTH_LABELS[node.health]

  const canRestart = node.id === "api" || node.id === "worker"

  const handleRestart = async () => {
    const key = node.id === "api" ? "api" : "worker"
    if (!confirm(`Restart ${node.label}? This will cause brief downtime.`)) return
    setRestarting(true)
    try {
      const result = await restartService(key)
      setMessage(result.success ? `Restarted ${node.label}` : `Failed: ${result.detail}`)
    } catch (e: any) {
      setMessage(`Error: ${e.message}`)
    }
    setRestarting(false)
    setTimeout(() => setMessage(""), 5000)
  }

  // Build detail rows based on node type
  const detailRows = Object.entries(node.detail).filter(([, v]) => v !== null && v !== undefined)

  // Extra context from snapshot for specific nodes
  const extraContext: Array<{ label: string; value: string | number }> = []
  if (node.id === "redis" && snapshot) {
    extraContext.push(
      { label: "Publisher Queue", value: snapshot.queue_pub },
      { label: "Priority Queue", value: snapshot.queue_pri },
      { label: "Default Queue", value: snapshot.queue_default },
      { label: "Total Depth", value: snapshot.queue_depth },
    )
  }
  if (node.id === "worker" && snapshot) {
    extraContext.push(
      { label: "Workers Alive", value: snapshot.gpu_workers_alive },
      { label: "Workers Ready", value: snapshot.gpu_workers_ready },
      { label: "Engine Ready", value: snapshot.engine_ready ? "Yes" : "No" },
      { label: "Avg Processing", value: `${snapshot.throughput_avg_s}s` },
    )
    const snap = snapshot as any
    if (snap.stalled_jobs > 0) {
      extraContext.push({ label: "Stalled Jobs", value: `${snap.stalled_jobs} (stuck at high %)` })
    }
    if (snap.slow_jobs > 0) {
      extraContext.push({ label: "Slow Jobs", value: `${snap.slow_jobs} (>3min SLO)` })
    }
    if (snap.longest_active_s > 0) {
      extraContext.push({ label: "Longest Active", value: `${(snap.longest_active_s / 60).toFixed(1)}min` })
    }
    // Individual GPU workers
    if (snapshot.gpu_workers && snapshot.gpu_workers.length > 0) {
      snapshot.gpu_workers.forEach(w => {
        extraContext.push({
          label: w.gpu_name || w.id,
          value: w.alive ? (w.ready ? `Ready (${w.gpu_utilization ?? 0}%)` : "Not Ready") : "Offline",
        })
      })
    }
  }
  if (node.id === "gpu" && snapshot) {
    extraContext.push(
      { label: "Utilization", value: snapshot.gpu_pct != null ? `${snapshot.gpu_pct}%` : "—" },
      { label: "Peak", value: snapshot.gpu_peak != null ? `${snapshot.gpu_peak}%` : "—" },
      { label: "Temperature", value: snapshot.gpu_temp != null ? `${snapshot.gpu_temp}°C` : "—" },
    )
  }
  if (node.id === "api" && snapshot) {
    extraContext.push(
      { label: "Throughput", value: `${snapshot.throughput_jobs_per_min}/min` },
      { label: "Active Jobs", value: snapshot.active_jobs },
      { label: "Critical Alerts", value: snapshot.alerts_critical },
      { label: "Warnings", value: snapshot.alerts_warn },
    )
  }
  if (node.id === "postgres" && snapshot) {
    extraContext.push(
      { label: "Disk Usage", value: snapshot.disk_pct != null ? `${Math.round(snapshot.disk_pct)}%` : "—" },
    )
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border/30">
        <Icon size={14} style={{ color: health.color }} />
        <span className="text-sm font-medium text-gray-200 flex-1">{node.label}</span>
        <div
          className={`w-2 h-2 rounded-full ${node.health !== "healthy" ? "animate-pulse" : ""}`}
          style={{ backgroundColor: health.color }}
        />
        <span className="text-[10px] font-medium" style={{ color: health.color }}>{health.label}</span>
        <button
          onClick={() => selectNode(null)}
          className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300"
        >
          <X size={13} />
        </button>
      </div>

      {/* Hero metric */}
      <div className="p-3 text-center border-b border-border/20">
        <div className="text-2xl font-mono font-bold" style={{ color: health.color }}>
          {node.metricValue}
        </div>
        <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">{node.metric}</div>
      </div>

      {/* Detail metrics from context */}
      {extraContext.length > 0 && (
        <div className="p-3 border-b border-border/20 space-y-1.5">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Details</div>
          {extraContext.map((row, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{row.label}</span>
              <span className="text-gray-300 font-mono">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Raw detail (fallback) */}
      {detailRows.length > 0 && extraContext.length === 0 && (
        <div className="p-3 border-b border-border/20 space-y-1.5">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Properties</div>
          {detailRows.map(([key, val]) => (
            <div key={key} className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{key.replace(/_/g, " ")}</span>
              <span className="text-gray-300 font-mono">{String(val)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {canRestart && (
        <div className="p-3 space-y-2">
          {message && <div className="text-xs text-accent">{message}</div>}
          <button
            onClick={handleRestart}
            disabled={restarting}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium border border-amber-500/20 disabled:opacity-50 transition-colors"
          >
            {restarting ? (
              <RefreshCw size={11} className="animate-spin" />
            ) : (
              <RotateCcw size={11} />
            )}
            Restart {node.label}
          </button>
        </div>
      )}
    </div>
  )
}
